import React, { Suspense, lazy, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsentFormService from '../services/consentFormService';
import { getConsentFormErrorKey } from '../components/consent-forms/consentFormDisplay';

// pdf.js is only needed once a customer actually has a form to read.
const ConsentFormViewerDialog = lazy(() => import('../components/consent-forms/ConsentFormViewerDialog'));

const NOTHING_PENDING = { accepted: true, acceptanceBatchId: null };

/**
 * Asks the signed-in customer to read and accept the consent forms a booking or purchase needs.
 * `ensureConsents` resolves once every form was accepted (or none was needed), or with
 * `accepted: false` if the customer closed the viewer. Render `consentDialog` in the page.
 */
const useConsentGate = (storeId) => {
  const { i18n } = useTranslation();
  const [session, setSession] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const resolveRef = useRef(null);

  const openViewer = useCallback((forms, request) => new Promise((resolve) => {
    resolveRef.current = resolve;
    setErrorKey(null);
    setSession({ forms, request });
  }), []);

  const finish = useCallback((result) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setSession(null);
    setErrorKey(null);
  }, []);

  const ensureConsents = useCallback(async ({
    serviceIds,
    serviceComboIds,
    servicePackageId,
    membershipPlanId,
    contextType,
    contextItemId,
    appointmentId,
  }) => {
    const pending = await ConsentFormService.getConsentRequirements(storeId, { serviceIds, serviceComboIds, servicePackageId, membershipPlanId });
    if (pending.length === 0) {
      return NOTHING_PENDING;
    }

    return openViewer(pending, { contextType, contextItemId, appointmentId });
  }, [openViewer, storeId]);

  /** Opens the viewer for forms already known to be pending, such as from the consents page. */
  const reviewForms = useCallback((forms, request) => (
    forms?.length ? openViewer(forms, request) : Promise.resolve(NOTHING_PENDING)
  ), [openViewer]);

  const handleAccept = useCallback(async (items) => {
    if (!session) {
      return;
    }

    try {
      setAccepting(true);
      setErrorKey(null);
      const result = await ConsentFormService.acceptConsentForms(storeId, {
        ...session.request,
        language: i18n.language,
        items,
      });
      finish({ accepted: true, acceptanceBatchId: result.acceptanceBatchId });
    } catch (acceptError) {
      console.error('Failed to accept consent forms:', acceptError);
      setErrorKey(getConsentFormErrorKey(acceptError, 'consentForms.errors.accept'));
    } finally {
      setAccepting(false);
    }
  }, [finish, i18n.language, session, storeId]);

  const loadFile = useCallback(
    (versionId) => ConsentFormService.getCustomerConsentFormFile(storeId, versionId),
    [storeId],
  );

  const consentDialog = session ? (
    <Suspense fallback={null}>
      <ConsentFormViewerDialog
        open
        forms={session.forms}
        loadFile={loadFile}
        accepting={accepting}
        errorKey={errorKey}
        onCancel={() => finish({ accepted: false, acceptanceBatchId: null })}
        onAccept={handleAccept}
      />
    </Suspense>
  ) : null;

  return { ensureConsents, reviewForms, consentDialog };
};

export default useConsentGate;
